import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ArrowsClockwise, Gift, PauseCircle, XCircle, HourglassLow, CircleNotch, BellRinging, PlayCircle, ClockCounterClockwise, ArrowUUpLeft, ShoppingCart, CalendarCheck, CheckCircle, CalendarX, Sliders, ArrowsLeftRight, DownloadSimple } from "@phosphor-icons/react";
import { RowanTerm } from "@/components/RowanTerm";
import { PaymentMethodSelect, paymentMethodLabel } from "@/lib/paymentMethods";

const label = { color: "var(--bk-text-2)" };

const EVENT_ICONS = {
  purchase: ShoppingCart, booking: CalendarCheck, visit_completed: CheckCircle,
  cancellation: CalendarX, credit_adjustment: Sliders, renewal: ArrowsClockwise,
  pause: PauseCircle, resume: PlayCircle, refund: ArrowUUpLeft, reschedule: ArrowsLeftRight,
};
const EVENT_COLORS = {
  purchase: "var(--bk-success)", booking: "var(--bk-accent)", visit_completed: "var(--bk-success)",
  cancellation: "#B23B1F", credit_adjustment: "#B08840", renewal: "var(--bk-accent)",
  pause: "#B08840", resume: "var(--bk-success)", refund: "#B23B1F", reschedule: "var(--bk-accent)",
};

function MemberTimeline({ businessId, clientName }) {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    api.get(`/businesses/${businessId}/wellness/activity`, { params: { client_name: clientName } })
      .then((r) => setEvents(r.data.events)).catch(() => setEvents([]));
  }, [businessId, clientName]);
  if (!events) return <p className="text-xs mt-3" style={label}>Loading history…</p>;
  if (!events.length) return <p className="text-xs mt-3" style={label} data-testid={`timeline-empty-${clientName}`}>No recorded activity yet.</p>;
  return (
    <div className="mt-3 pt-3 border-t space-y-2 max-h-64 overflow-y-auto" style={{ borderColor: "var(--bk-border)" }} data-testid={`timeline-${clientName}`}>
      {events.map((e) => {
        const Icon = EVENT_ICONS[e.type] || Sliders;
        return (
          <div key={e.id} className="flex items-start gap-2" data-testid={`timeline-event-${e.id}`}>
            <Icon size={15} weight="fill" color={EVENT_COLORS[e.type] || "var(--bk-text-2)"} className="mt-0.5 shrink-0" />
            <div>
              <p className="text-xs" style={{ color: "var(--bk-text)" }}>
                <span className="font-semibold">{e.label}</span> — {e.detail}
              </p>
              <p className="text-[10px]" style={label}>{(e.at || "").slice(0, 10)} · by {e.actor === "client" ? "the client" : e.actor === "system" ? "Rowan" : "you"}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CreditRow({ c, businessId, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [renewMethod, setRenewMethod] = useState(c.payment_method || "bank");
  const total = c.sessions_total || 0;
  const used = c.sessions_used || 0;
  const remaining = c.sessions_remaining ?? Math.max(0, total - used);
  const pct = total ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const isGift = c.kind === "gift_card";
  const status = c.computed_status || c.status;

  const call = async (path, msgFallback, body = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(path, body);
      toast.success(data.rowan_message || msgFallback);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't do that."); }
    finally { setBusy(false); }
  };

  return (
    <div className="bk-card p-4" data-testid={`credit-${c.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-sm" style={{ color: "var(--bk-text)" }}>{c.client_name}</p>
          <p className="text-xs mt-0.5" style={label}>
            {c.item_name}
            {c.rolled_over ? ` · ${c.rolled_over} rolled over` : ""}
            {c.expires_at ? ` · expires ${c.expires_at}` : ""}
            {c.renews_at ? ` · renews ${c.renews_at}` : ""}
          </p>
          <p className="text-[11px] mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }} data-testid={`credit-method-${c.id}`}>
            Paid via {paymentMethodLabel(c.payment_method)}{!c.payment_method ? " (default)" : ""}
          </p>
        </div>
        {isGift ? (
          <span className="text-xs font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-accent)" }}>
            <Gift size={13} weight="fill" /> ${Number(c.gift_card_balance || 0).toFixed(2)} left
          </span>
        ) : (
          <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }} data-testid={`credit-remaining-${c.id}`}>
            {remaining} of {total} left
          </span>
        )}
      </div>
      {!isGift && total > 0 && (
        <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--bk-border)" }}>
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "var(--bk-accent)" }} />
        </div>
      )}
      {status === "active" && !isGift && (
        <div className="flex flex-wrap gap-2 mt-3">
          {remaining > 0 && (
            <button disabled={busy} onClick={() => call(`/businesses/${businessId}/client-credits/${c.id}/use`, "Session logged.")} data-testid={`use-session-${c.id}`}
              className="px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-50" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
              {busy ? <CircleNotch size={13} className="animate-spin inline" /> : "Use 1 session"}
            </button>
          )}
          <span className="inline-flex items-center gap-1">
            <button disabled={busy} onClick={() => call(`/businesses/${businessId}/client-credits/${c.id}/renew`, "Renewed.", { payment_method: renewMethod })} data-testid={`renew-${c.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-50" style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}>
              <ArrowsClockwise size={13} weight="bold" /> Renew
            </button>
            <PaymentMethodSelect compact value={renewMethod} onChange={setRenewMethod} testId={`renew-method-${c.id}`} />
          </span>
          <button disabled={busy} onClick={() => call(`/businesses/${businessId}/client-credits/${c.id}/pause`, "Paused.")} data-testid={`pause-${c.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-50" style={{ borderColor: "var(--bk-border)", color: "#B08840" }}>
            <PauseCircle size={13} weight="bold" /> Pause
          </button>
          <button disabled={busy} onClick={() => { if (window.confirm(`Refund the unused value on ${c.item_name} for ${c.client_name} via ${paymentMethodLabel(renewMethod)} and close it out?`)) call(`/businesses/${businessId}/client-credits/${c.id}/refund`, "Refunded.", { payment_method: renewMethod }); }} data-testid={`refund-${c.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-50" style={{ borderColor: "var(--bk-border)", color: "#B23B1F" }}>
            <ArrowUUpLeft size={13} weight="bold" /> Refund
          </button>
        </div>
      )}
      {status === "paused" && !isGift && (
        <div className="mt-3">
          <button disabled={busy} onClick={() => call(`/businesses/${businessId}/client-credits/${c.id}/resume`, "Resumed.")} data-testid={`resume-${c.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-50" style={{ borderColor: "var(--bk-border)", color: "var(--bk-success)" }}>
            <PlayCircle size={13} weight="bold" /> Resume
          </button>
        </div>
      )}
      {(status === "expired" || status === "cancelled") && !isGift && (
        <div className="mt-3">
          <span className="inline-flex items-center gap-1">
            <button disabled={busy} onClick={() => call(`/businesses/${businessId}/client-credits/${c.id}/renew`, "Renewed.", { payment_method: renewMethod })} data-testid={`renew-${c.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-50" style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}>
              <ArrowsClockwise size={13} weight="bold" /> Renew &amp; reactivate
            </button>
            <PaymentMethodSelect compact value={renewMethod} onChange={setRenewMethod} testId={`renew-method-${c.id}`} />
          </span>
        </div>
      )}
      <button onClick={() => setShowHistory((s) => !s)} data-testid={`history-${c.id}`}
        className="inline-flex items-center gap-1.5 mt-3 text-[11px] font-semibold" style={{ color: "var(--bk-text-2)" }}>
        <ClockCounterClockwise size={12} weight="bold" /> {showHistory ? "Hide history" : "Full history"}
      </button>
      {showHistory && <MemberTimeline businessId={businessId} clientName={c.client_name} />}
    </div>
  );
}

function Section({ title, icon: Icon, color, rows, businessId, onChanged, testid }) {
  if (!rows?.length) return null;
  return (
    <div data-testid={testid}>
      <h3 className="text-xs uppercase tracking-[0.2em] font-semibold mb-3 inline-flex items-center gap-1.5" style={{ color }}>
        {Icon && <Icon size={14} weight="fill" />} {title} ({rows.length})
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((c) => <CreditRow key={c.id} c={c} businessId={businessId} onChanged={onChanged} />)}
      </div>
    </div>
  );
}

export function MembershipTracker({ businessId, refreshKey = 0 }) {
  const [data, setData] = useState(null);

  const load = () => api.get(`/businesses/${businessId}/memberships/tracker`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId, refreshKey]);

  const download = async (kind) => {
    try {
      const res = await api.get(`/businesses/${businessId}/wellness/export/${kind}.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = kind === "members" ? "members.csv" : "member-activity.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded — client names and balances only, no personal contact or health data.");
    } catch { toast.error("Couldn't export."); }
  };

  if (!data) return <p className="text-sm" style={label} data-testid="tracker-loading">Loading…</p>;
  if (data.error) return <p className="text-sm" style={label} data-testid="tracker-error">Couldn't load the membership tracker. Try again in a moment.</p>;

  const chips = [
    { k: "sold", label: "Sessions sold", v: data.sessions_sold },
    { k: "used", label: "Delivered", v: data.sessions_used },
    { k: "remaining", label: "Still owed", v: data.sessions_remaining },
    { k: "unearned", label: "Unearned money", v: `$${Number(data.unearned_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
    { k: "gift", label: "Gift-card balance", v: `$${Number(data.gift_card_balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
  ];

  return (
    <div className="space-y-6" data-testid="membership-tracker">
      <div className="flex justify-end gap-2">
        <button onClick={() => download("members")} data-testid="export-members-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}>
          <DownloadSimple size={13} weight="bold" /> Export members CSV
        </button>
        <button onClick={() => download("activity")} data-testid="export-activity-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}>
          <DownloadSimple size={13} weight="bold" /> Export activity CSV
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="tracker-summary">
        {chips.map((c) => (
          <div key={c.k} className="bk-card p-4" data-testid={`tracker-chip-${c.k}`}>
            <p className="text-xs" style={label}>{c.k === "unearned" ? <RowanTerm term="unearned_revenue" size={12}>{c.label}</RowanTerm> : c.label}</p>
            <p className="font-display text-xl font-bold mt-1" style={{ color: "var(--bk-text)" }}>{c.v}</p>
          </div>
        ))}
      </div>
      <p className="text-sm italic leading-relaxed max-w-3xl" style={label} data-testid="tracker-rowan-summary">Rowan: “{data.rowan_summary}”</p>

      {data.reminders?.length > 0 && (
        <div className="bk-card p-4" style={{ borderColor: "rgba(232,168,56,0.5)", background: "#FFF9F0" }} data-testid="tracker-reminders">
          <div className="flex items-center gap-2 mb-2">
            <BellRinging size={18} weight="fill" color="#E8A838" />
            <p className="font-semibold text-sm" style={{ color: "var(--bk-text)" }}>Renewals &amp; expirations in the next 7 days</p>
          </div>
          <div className="space-y-1.5">
            {data.reminders.map((r) => (
              <p key={r.credit_id} className="text-sm" style={{ color: "var(--bk-text)" }} data-testid={`reminder-${r.credit_id}`}>
                <span className="font-semibold">{r.client_name}</span>'s {r.item_name} {r.event === "renews" ? "renews" : "expires"} {r.days_left === 0 ? "today" : `in ${r.days_left} day${r.days_left !== 1 ? "s" : ""}`}
                {r.event === "expires" && r.sessions_remaining > 0 ? ` — ${r.sessions_remaining} session${r.sessions_remaining !== 1 ? "s" : ""} still unused` : ""}.
              </p>
            ))}
          </div>
        </div>
      )}

      <Section title="Renewing in the next 30 days" icon={ArrowsClockwise} color="#E8A838" rows={data.upcoming_renewals} businessId={businessId} onChanged={load} testid="tracker-renewals" />
      <Section title="Active" color="var(--bk-success)" rows={data.active.filter((c) => !data.upcoming_renewals.some((r) => r.id === c.id))} businessId={businessId} onChanged={load} testid="tracker-active" />
      <Section title="Paused" icon={PauseCircle} color="#B08840" rows={data.paused} businessId={businessId} onChanged={load} testid="tracker-paused" />
      <Section title="Cancelled" icon={XCircle} color="#B23B1F" rows={data.cancelled} businessId={businessId} onChanged={load} testid="tracker-cancelled" />
      <Section title="Expired" icon={HourglassLow} color="var(--bk-text-2)" rows={data.expired} businessId={businessId} onChanged={load} testid="tracker-expired" />
    </div>
  );
}
